'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const ROSE = '#C4688A';
const ROSE_LIGHT = '#F5E6EE';
const ROSE_BORDER = '#EAC8D8';
const ROSE_RGB = '196,104,138';
const TEXT_DARK = '#1a1a2e';
const TEXT_MID = 'rgba(26,26,46,0.6)';
const TEXT_LIGHT = 'rgba(26,26,46,0.4)';
const BG = '#FAFAF8';

const SCALE_LABELS: Record<number, string> = {
  1: 'Not at all',
  2: 'Somewhat',
  3: 'Quite a bit',
  4: 'Absolutely',
};

interface RankValue {
  interest: number;
  experience: number;
}

interface Rankings {
  [key: string]: RankValue;
}

const INITIAL_RANKINGS: Rankings = {
  energy_crowd: { interest: 0, experience: 0 },
  starting_conversations: { interest: 0, experience: 0 },
  turning_action: { interest: 0, experience: 0 },
  independent_hustle: { interest: 0, experience: 0 },
  performance_pay: { interest: 0, experience: 0 },
};

function RankInline({
  id,
  label,
  description,
  rankings,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  rankings: Rankings;
  onChange: (id: string, type: 'interest' | 'experience', val: number) => void;
}) {
  const val = rankings[id] || { interest: 0, experience: 0 };
  return (
    <div
      className="my-8 rounded-2xl p-6"
      style={{ background: ROSE_LIGHT, border: `1px solid ${ROSE_BORDER}` }}
    >
      <div className="flex items-start gap-3 mb-5">
        <span className="text-lg" style={{ color: ROSE }}>✦</span>
        <div>
          <p className="text-sm font-black" style={{ color: TEXT_DARK, letterSpacing: '-0.01em' }}>
            Rate Yourself: {label}
          </p>
          <p className="text-xs mt-0.5" style={{ color: TEXT_MID }}>{description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <RankRow
          label="Interest"
          value={val.interest}
          onChange={(v) => onChange(id, 'interest', v)}
        />
        <RankRow
          label="Experience"
          value={val.experience}
          onChange={(v) => onChange(id, 'experience', v)}
        />
      </div>
    </div>
  );
}

function RankRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <p
        className="text-[10px] font-bold mb-2"
        style={{ color: TEXT_MID, letterSpacing: '0.06em', textTransform: 'uppercase' }}
      >
        {label}
      </p>
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4].map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className="flex-1 py-2 rounded-xl text-sm font-black transition-all duration-150 hover:scale-105 active:scale-95"
              style={{
                background: active ? ROSE : '#fff',
                color: active ? '#fff' : TEXT_LIGHT,
                border: `1.5px solid ${active ? ROSE : ROSE_BORDER}`,
                boxShadow: active ? `0 4px 12px rgba(196,104,138,0.3)` : 'none',
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      {value > 0 && (
        <p className="text-[10px] mt-1 font-semibold" style={{ color: ROSE }}>
          {SCALE_LABELS[value]}
        </p>
      )}
    </div>
  );
}

export default function JobInterestPage() {
  const router = useRouter();
  const [rankings, setRankings] = useState<Rankings>(INITIAL_RANKINGS);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const [applicantData, setApplicantData] = useState<Record<string, unknown>>({});
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('modelApplyData');
      if (raw) setApplicantData(JSON.parse(raw));
    } catch {
      // no-op
    }
  }, []);

  const setRank = (id: string, type: 'interest' | 'experience', value: number) => {
    setRankings((prev) => ({
      ...prev,
      [id]: { ...prev[id], [type]: value },
    }));
    setError('');
  };

  const allRanked = Object.keys(INITIAL_RANKINGS).every(
    (key) => rankings[key].interest > 0 && rankings[key].experience > 0
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!phone.trim()) {
      setError('Please enter your cell phone number.');
      return;
    }
    if (!allRanked) {
      setError('Please rate both your interest and experience for every section before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...applicantData, name: name.trim(), phone: phone.trim(), rankings };
      const res = await fetch('/api/submit-rep-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        // Store partial application data for the full application page
        sessionStorage.setItem('repApplicationId', data.id);
        sessionStorage.setItem('repApplicantName', name.trim());
        sessionStorage.setItem('repApplicantPhone', phone.trim());
        // Store rankings so RepSignupFlow can pre-fill Step 3
        sessionStorage.setItem('repApplicantRankings', JSON.stringify(rankings));
        // Navigate to the full application page
        router.push('/rep-signup');
      } else {
        setError('Something went wrong saving your application. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: BG }}>
        <div className="max-w-md w-full text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8"
            style={{ background: ROSE_LIGHT, border: `2px solid ${ROSE_BORDER}` }}
          >
            <span className="text-3xl" style={{ color: ROSE }}>✦</span>
          </div>
          <h1
            className="text-3xl font-black mb-4 tracking-tight"
            style={{ color: TEXT_DARK, letterSpacing: '-0.03em' }}
          >
            You're All Set
          </h1>
          <p className="text-base leading-relaxed mb-8" style={{ color: TEXT_MID }}>
            Your application and rankings have been submitted. Our team will review everything and be in touch within 72 hours.
          </p>
          <div
            className="rounded-2xl p-6 mb-8 text-left"
            style={{ background: ROSE_LIGHT, border: `1px solid ${ROSE_BORDER}` }}
          >
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: ROSE }}>
              What happens next
            </p>
            {[
              'We review your application & rankings',
              'You hear from us within 72 hours',
              'Onboarding takes 30 minutes — earn at your first event within the week',
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3 mb-2 last:mb-0">
                <span className="text-xs font-black mt-0.5" style={{ color: ROSE }}>{i + 1}.</span>
                <p className="text-sm" style={{ color: TEXT_DARK }}>{step}</p>
              </div>
            ))}
          </div>
          <Link
            href="/rep-landing"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: ROSE }}
          >
            ← Back to Get Ahead
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: BG, fontFamily: '"DM Sans", system-ui, sans-serif' }}
    >
      {/* Nav */}
      <nav
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-14 py-4"
        style={{
          background: 'rgba(250,250,248,0.94)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid rgba(${ROSE_RGB},0.12)`,
        }}
      >
        <Link href="/rep-landing" className="flex items-center gap-2">
          <span
            className="font-black text-lg tracking-tight"
            style={{ color: TEXT_DARK, letterSpacing: '-0.04em' }}
          >
            Get Ahead
          </span>
          <span
            className="text-[9px] px-2 py-0.5 rounded-full font-bold tracking-[0.18em] uppercase hidden sm:block"
            style={{ background: ROSE_LIGHT, color: ROSE, border: `1px solid ${ROSE_BORDER}` }}
          >
            Agency
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: ROSE }} />
          <span className="text-xs font-semibold" style={{ color: TEXT_MID }}>
            Role Assessment
          </span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 md:px-8 py-14 md:py-20">
        {/* Header */}
        <div className="mb-12">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase mb-6"
            style={{ background: ROSE_LIGHT, color: ROSE, border: `1px solid ${ROSE_BORDER}` }}
          >
            FindACut · Brand Ambassador
          </div>
          <h1
            className="font-black leading-tight mb-4"
            style={{
              fontSize: 'clamp(30px, 5vw, 52px)',
              letterSpacing: '-0.04em',
              color: TEXT_DARK,
            }}
          >
            Brand Ambassador — FindACut
          </h1>
          <p className="text-base leading-relaxed" style={{ color: TEXT_MID }}>
            Read through the role below. Rate yourself as you go — both your{' '}
            <strong style={{ color: TEXT_DARK }}>interest</strong> and{' '}
            <strong style={{ color: TEXT_DARK }}>experience</strong> for each section on a scale of 1 to 4.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Name & Phone */}
          <div
            className="rounded-3xl p-8 md:p-10 mb-6"
            style={{
              background: '#fff',
              border: `1px solid ${ROSE_BORDER}`,
              boxShadow: `0 4px 40px rgba(${ROSE_RGB},0.07)`,
            }}
          >
            <p
              className="text-xs font-bold tracking-[0.14em] uppercase mb-6"
              style={{ color: ROSE }}
            >
              Let's start with you
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="applicant-name"
                  className="block text-xs font-bold mb-2"
                  style={{ color: TEXT_MID, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                >
                  Full Name
                </label>
                <input
                  id="applicant-name"
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(''); }}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-150"
                  style={{
                    background: ROSE_LIGHT,
                    border: `1.5px solid ${ROSE_BORDER}`,
                    color: TEXT_DARK,
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = ROSE; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = ROSE_BORDER; }}
                />
              </div>
              <div>
                <label
                  htmlFor="applicant-phone"
                  className="block text-xs font-bold mb-2"
                  style={{ color: TEXT_MID, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                >
                  Cell Phone
                </label>
                <input
                  id="applicant-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setError(''); }}
                  placeholder="(206) 555-0100"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-150"
                  style={{
                    background: ROSE_LIGHT,
                    border: `1.5px solid ${ROSE_BORDER}`,
                    color: TEXT_DARK,
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = ROSE; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = ROSE_BORDER; }}
                />
              </div>
            </div>
          </div>

          <div
            className="rounded-3xl p-8 md:p-10"
            style={{
              background: '#fff',
              border: `1px solid ${ROSE_BORDER}`,
              boxShadow: `0 4px 40px rgba(${ROSE_RGB},0.07)`,
            }}
          >
            {/* Intro */}
            <div className="mb-10 pb-10" style={{ borderBottom: `1px solid rgba(${ROSE_RGB},0.12)` }}>
              <p
                className="text-lg font-semibold leading-relaxed mb-4"
                style={{ color: TEXT_DARK }}
              >
                Love sports, big crowds, and being the reason something actually happens?
              </p>
              <p className="text-base leading-relaxed" style={{ color: TEXT_MID }}>
                We're building a high-energy, female-forward promo team to work live sporting events
                across Seattle — Seahawks, Mariners, Kraken, Sounders… all of it. If you've got
                confidence, presence, and know how to turn attention into action, you're exactly who we're looking for.
              </p>
            </div>

            {/* Section 1 — Energy & Crowd Presence */}
            <div className="mb-10 pb-10" style={{ borderBottom: `1px solid rgba(${ROSE_RGB},0.12)` }}>
              <RankInline
                id="energy_crowd"
                label="Energy & Crowd Presence"
                description="How naturally do you stand out and engage in high-energy environments?"
                rankings={rankings}
                onChange={setRank}
              />

              <p className="text-base leading-relaxed mt-6" style={{ color: TEXT_MID }}>
                This is not a passive role. You're not collecting names — you're setting real
                appointments on the spot. You start the conversation, create the moment, and guide
                it to one simple outcome: they're booked.
              </p>
              <p className="text-base leading-relaxed mt-4" style={{ color: TEXT_MID }}>
                It's quick. It's fun. It's a little competitive. And once you feel it click, it's
                hard to stop.
              </p>
            </div>

            {/* Section 2 — Starting Conversations */}
            <div className="mb-10 pb-10" style={{ borderBottom: `1px solid rgba(${ROSE_RGB},0.12)` }}>
              <RankInline
                id="starting_conversations"
                label="Starting Conversations with Strangers"
                description="Walking up, opening strong, and keeping it natural"
                rankings={rankings}
                onChange={setRank}
              />

              <p className="text-base leading-relaxed mt-6" style={{ color: TEXT_MID }}>
                You'll be moving through crowds, reading people, picking your moments, and turning
                10–20 second interactions into confirmed bookings. No long pitches, no awkward
                scripts — just a simple flow that works when you bring the right energy.
              </p>
              <p className="text-base leading-relaxed mt-4" style={{ color: TEXT_MID }}>
                There's no "sales pressure" here, but let's be real — you are there to close. The
                difference is… it doesn't feel like closing. It feels like momentum.
              </p>
            </div>

            {/* Section 3 — Turning Conversations into Action */}
            <div className="mb-10 pb-10" style={{ borderBottom: `1px solid rgba(${ROSE_RGB},0.12)` }}>
              <RankInline
                id="turning_action"
                label="Turning Conversations into Action"
                description={`Guiding someone from "that sounds good" to "I'm booked"`}
                rankings={rankings}
                onChange={setRank}
              />

              <p className="text-base leading-relaxed mt-6" style={{ color: TEXT_MID }}>
                You'll be working inside some of the most electric environments in the city —
                packed stadiums, loud crowds, nonstop energy. If you like being in the middle of it
                instead of watching from the sidelines, this is your lane.
              </p>
              <p className="text-base leading-relaxed mt-4" style={{ color: TEXT_MID }}>
                And when you're not at events? You can still run it. Bars, gyms, downtown,
                festivals — anywhere people are out, you can create your own opportunities and
                stack appointments on your own time.
              </p>
            </div>

            {/* Section 4 — Independent Hustle */}
            <div className="mb-10 pb-10" style={{ borderBottom: `1px solid rgba(${ROSE_RGB},0.12)` }}>
              <RankInline
                id="independent_hustle"
                label="Independent Hustle"
                description="Making money on your own time without being managed"
                rankings={rankings}
                onChange={setRank}
              />

              <p className="text-base leading-relaxed mt-6" style={{ color: TEXT_MID }}>
                Now let's talk money — because that's the fun part.
              </p>
              <p className="text-base leading-relaxed mt-4" style={{ color: TEXT_MID }}>
                You get paid for confirmed appointments. Not conversations. Not "maybes." Results.
              </p>
              <p className="text-base leading-relaxed mt-4 font-semibold" style={{ color: TEXT_DARK }}>
                Paid weekly. No cap.
              </p>
              <p className="text-base leading-relaxed mt-4" style={{ color: TEXT_MID }}>
                Some people treat this like a side hustle. Others realize pretty quickly they can
                turn it into something much bigger.
              </p>
            </div>

            {/* Section 5 — Performance-Based Pay */}
            <div className="mb-10">
              <RankInline
                id="performance_pay"
                label="Performance-Based Pay"
                description="Earning based on results, not hourly"
                rankings={rankings}
                onChange={setRank}
              />

              <p className="text-base leading-relaxed mt-6" style={{ color: TEXT_MID }}>
                The people who win here all have the same traits: They're naturally social. Slightly
                bold. A little competitive. They don't wait, they don't hesitate, and they know how
                to move a moment forward without making it weird.
              </p>
              <p className="text-base leading-relaxed mt-4" style={{ color: TEXT_MID }}>
                If you can walk into a crowd and feel like you belong there — you'll feel right at
                home here.
              </p>
              <p
                className="text-base leading-relaxed mt-6 font-semibold"
                style={{ color: TEXT_DARK }}
              >
                So let's end with the real question —
              </p>
              <p
                className="text-lg leading-relaxed mt-3 font-black"
                style={{ color: TEXT_DARK, letterSpacing: '-0.02em' }}
              >
                Can you walk up to someone, create a moment in seconds… and confidently get them
                booked before they even think twice?
              </p>
              <p
                className="text-base leading-relaxed mt-4 font-semibold"
                style={{ color: ROSE }}
              >
                If yes — let's get you in the game.
              </p>
            </div>
          </div>

          {/* Submit */}
          {error && (
            <p className="mt-6 mb-2 text-sm font-semibold text-center" style={{ color: '#E05A7A' }}>
              {error}
            </p>
          )}

          <div className="flex flex-col items-center gap-4 mt-8">
            <button
              type="submit"
              disabled={submitting}
              className="w-full max-w-sm py-4 rounded-2xl font-black text-sm tracking-wide transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
              style={{
                background: ROSE,
                color: '#fff',
                letterSpacing: '0.04em',
                boxShadow: `0 8px 32px rgba(${ROSE_RGB},0.35)`,
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
            <p className="text-xs text-center" style={{ color: TEXT_LIGHT }}>
              We respond to every application within 72 hours.
            </p>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div
        className="text-center py-8 px-6 mt-6"
        style={{ borderTop: `1px solid rgba(${ROSE_RGB},0.12)` }}
      >
        <p className="text-xs" style={{ color: TEXT_LIGHT }}>
          FindACut.com — Connecting men with great local barbers.{' '}
          <span className="block sm:inline mt-1 sm:mt-0">
            1099 independent contractor. You are responsible for your own taxes on all earnings.
          </span>
        </p>
      </div>
    </div>
  );
}
